import { piiSelector, srcPrefix } from '../config';
import { visibleText } from '../elementText';
import { BUG_OWN_ATTR } from '../recorder';

/**
 * Places a question is about: an element (click), selected text, an area (drag a frame) or the whole screen. A place
 * goes to the host as the `file:line` marks its build put on elements (`sourceAttr`), from the place up the page, plus
 * its visible text — the host finds the code by both.
 */

export type TargetKind = 'element' | 'text' | 'area' | 'screen';

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Target {
  id: number;
  kind: TargetKind;
  anchors: string[];
  text: string;
  /** Tooltip of the place when it is not its text (a full name behind a short tag); never from masked elements. */
  hint?: string;
  /** Outlined element; for selected text — the common ancestor. */
  el: Element | null;
  /** Area frame in page coordinates (window scroll at the time of the pick). */
  box?: Box;
}

export const MAX_ANCHORS = 8;
/** Places per question — as many as facts' server takes (`MAX_TARGETS`). */
export const MAX_TARGETS = 6;
/** Elements inside an area sent as marks. */
const AREA_ROOTS = 6;
export const OWN = `[${BUG_OWN_ATTR}]`;

let nextId = 1;
export const newTargetId = (): number => nextId++;

export const squash = (text: string, max: number): string => text.replace(/\s+/g, ' ').trim().slice(0, max);

const withPrefix = (value: string): string => `${srcPrefix()}${value}`;

export function anchorsOf(attr: string, start: Element | null, limit = MAX_ANCHORS): string[] {
  const out: string[] = [];
  let el: Element | null = start?.closest(`[${attr}]`) ?? null;
  while (el && out.length < limit) {
    const value = el.getAttribute(attr);
    if (value && !out.includes(withPrefix(value))) out.push(withPrefix(value));
    el = el.parentElement?.closest(`[${attr}]`) ?? null;
  }
  return out;
}

/** Text as in the markup: text pieces joined by spaces (not `innerText`: it returns `text-transform` capitals). */
export function elementText(el: Element): string {
  const own = visibleText(el);
  const label = el.getAttribute('aria-label') || el.getAttribute('title') || '';
  return squash(own || label, 300);
}

/** Levels up the page a tooltip is looked for: a chip's `title` often sits on its wrapper. */
const HINT_LEVELS = 3;

/** The place's tooltip — `title` on it or a few levels up — unless it repeats the text or sits in a masked element. */
export function hintOf(el: Element, text: string): string {
  const pii = piiSelector();
  let node: Element | null = el;
  for (let level = 0; node && level <= HINT_LEVELS; level += 1, node = node.parentElement) {
    if (node.closest(OWN)) return '';
    const title = squash(node.getAttribute('title') ?? '', 200);
    if (!title) continue;
    if (node.closest(pii)) return '';
    return title === text ? '' : title;
  }
  return '';
}

export function elementTarget(attr: string, el: Element): Target {
  const text = elementText(el);
  return { id: newTargetId(), kind: 'element', anchors: anchorsOf(attr, el), text, hint: hintOf(el, text) || undefined, el };
}

export function screenTarget(attr: string): Target {
  const main = document.querySelector('main') ?? document.body;
  return { id: newTargetId(), kind: 'screen', anchors: anchorsOf(attr, main.querySelector(`[${attr}]`)), text: '', el: null };
}

/** Marks of the blocks visible on screen a question without a place carries (Тв7а; the service takes as many). */
export const MAX_SCREEN_MARKS = 12;

/**
 * No place picked (Тв7а): the `attr` marks of the blocks on screen — in the viewport, not the widget's own, not hidden —
 * in page order, each once, at most `limit`. Weak anchors: the service looks at their code, the question keeps no place.
 */
export function visibleMarks(attr: string, limit = MAX_SCREEN_MARKS): string[] {
  const out: string[] = [];
  const width = window.innerWidth || document.documentElement.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight;
  for (const el of Array.from(document.querySelectorAll(`[${attr}]`))) {
    if (out.length >= limit) break;
    if (el.closest(OWN)) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;
    if (rect.bottom <= 0 || rect.right <= 0 || rect.top >= height || rect.left >= width) continue;
    const value = el.getAttribute(attr);
    if (value && !out.includes(withPrefix(value))) out.push(withPrefix(value));
  }
  return out;
}

/**
 * Area frame → a place: marks of the elements lying wholly inside, top ones only (nested in another such element are
 * not repeated), up to `AREA_ROOTS`; plus two marks of what holds the whole frame; text — their labels.
 */
export function areaTarget(attr: string, box: Box): Target {
  const right = box.left + box.width;
  const bottom = box.top + box.height;
  const inside = Array.from(document.querySelectorAll(`[${attr}]`)).filter((el) => {
    if (el.closest(OWN)) return false;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    return rect.left >= box.left - 2 && rect.top >= box.top - 2 && rect.right <= right + 2 && rect.bottom <= bottom + 2;
  });
  const set = new Set(inside);
  const roots = inside.filter((el) => {
    const parent = el.parentElement?.closest(`[${attr}]`);
    return !parent || !set.has(parent);
  });
  const center =
    typeof document.elementsFromPoint === 'function'
      ? document.elementsFromPoint(box.left + box.width / 2, box.top + box.height / 2).find((el) => !el.closest(OWN))
      : undefined;
  let holder: Element | null = center?.closest(`[${attr}]`) ?? null;
  while (holder) {
    const rect = holder.getBoundingClientRect();
    if (rect.left <= box.left && rect.top <= box.top && rect.right >= right && rect.bottom >= bottom) break;
    holder = holder.parentElement?.closest(`[${attr}]`) ?? null;
  }
  const anchors: string[] = [];
  for (const el of roots) {
    const value = el.getAttribute(attr);
    if (value && !anchors.includes(withPrefix(value))) anchors.push(withPrefix(value));
    if (anchors.length >= AREA_ROOTS) break;
  }
  for (const anchor of anchorsOf(attr, holder ?? center ?? null, 2)) if (!anchors.includes(anchor)) anchors.push(anchor);
  const text = squash(roots.map((el) => elementText(el)).filter(Boolean).join(' · '), 300);
  return {
    id: newTargetId(),
    kind: 'area',
    anchors,
    text,
    el: null,
    box: { left: box.left + window.scrollX, top: box.top + window.scrollY, width: box.width, height: box.height },
  };
}

/** Add a place: "whole screen" replaces the rest and the other way round; the same element twice — once. */
export function withTarget(list: Target[], next: Target): Target[] {
  const rest = next.kind === 'screen' ? [] : list.filter((item) => item.kind !== 'screen');
  const dup = next.el && rest.some((item) => item.el === next.el && item.kind === next.kind);
  return dup ? rest : [...rest, next].slice(0, MAX_TARGETS);
}

export function excerpt(target: Target): string {
  if (target.kind === 'screen') return 'Весь экран';
  const text = squash(target.text, 40);
  const more = target.text.length > 40 ? '…' : '';
  if (target.kind === 'area') return text ? `Область: «${text}${more}»` : 'Область';
  return text ? `«${text}${more}»` : 'Элемент';
}

export function placesLabel(targets: Target[]): string {
  if (targets.length === 0) return 'Экран';
  if (targets.length === 1) return excerpt(targets[0]);
  const n = targets.length;
  return `${n} ${n >= 2 && n <= 4 ? 'места' : 'мест'}`;
}
